import { useCallback, useEffect, useRef, useState } from 'react'
import type { PromptFilter, PromptTemplate } from '@saip/backend'
import { useBackend } from './context'

export interface UsePrompts {
  prompts: PromptTemplate[]
  loading: boolean
  /** Last failed reload, or null. The previous list stays visible meanwhile. */
  error: Error | null
  reload: (filter?: PromptFilter) => Promise<void>
  save: (
    input: PromptTemplate | Omit<PromptTemplate, 'id' | 'version' | 'updatedAt'>,
  ) => Promise<void>
  remove: (id: string) => Promise<void>
}

const CACHE_KEY = 'saip.prompts.cache.v1'

/** Last list that came back — shown immediately on the next start. */
function readCache(): PromptTemplate[] {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return Array.isArray(parsed) ? (parsed as PromptTemplate[]) : []
  } catch {
    return []
  }
}

function writeCache(items: PromptTemplate[]): void {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(items))
  } catch {
    // Storage blocked — the in-memory list still drives this session.
  }
}

export function usePrompts(): UsePrompts {
  const backend = useBackend()
  const [prompts, setPrompts] = useState<PromptTemplate[]>(() => readCache())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  // The backend switches from the built-in store to the server once the probe
  // answers, so two loads can be in flight. Results apply in order: a late
  // answer never overwrites a newer one.
  const seq = useRef(0)
  const applied = useRef(0)

  const reload = useCallback(
    async (filter?: PromptFilter) => {
      const mine = ++seq.current
      setLoading(true)
      try {
        const list = await backend.prompts.list(filter)
        if (mine < applied.current) return
        applied.current = mine
        setPrompts(list)
        setError(null)
        if (!filter) writeCache(list)
      } catch (e) {
        if (mine < applied.current) return
        // Keep whatever is shown (cache or previous list) — an empty screen
        // helps nobody.
        setError(e instanceof Error ? e : new Error(String(e)))
      } finally {
        if (mine === seq.current) setLoading(false)
      }
    },
    [backend],
  )

  useEffect(() => {
    void reload()
  }, [reload])

  const save = useCallback(
    async (input: PromptTemplate | Omit<PromptTemplate, 'id' | 'version' | 'updatedAt'>) => {
      if ('id' in input && input.id) {
        await backend.prompts.update?.(input.id, input)
      } else {
        await backend.prompts.create?.(input)
      }
      await reload()
    },
    [backend, reload],
  )

  const remove = useCallback(
    async (id: string) => {
      await backend.prompts.delete?.(id)
      await reload()
    },
    [backend, reload],
  )

  return { prompts, loading, error, reload, save, remove }
}
